import { CdkDrag, CdkDragHandle, Point } from '@angular/cdk/drag-drop';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, NgZone, TemplateRef, Type, afterNextRender, computed, effect, inject, input, output, untracked, viewChild, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from '@shared/shared.module';
import { FloatingPanelDockTab, FloatingPanelRef } from './floating-panel.types';
import { FLOATING_PANEL_HEADER_ACTIONS, FloatingPanelHeaderAction } from './floating-panel-header-actions.token';
import { FLOATING_PANEL_TOPBAR_LEFT } from './floating-panel-topbar-content.token';
import { FLOATING_PANEL_REF } from './floating-panel-ref.token';
import { floatingPanelLockTooltip, floatingPanelUnlockTooltip } from './floating-panel-lock.texts';

/**
 * Minimumafstand tussen de bovenrand van het paneel en de bovenrand van de
 * viewport bij slepen. Zonder deze rand kan een gebruiker het paneel boven
 * de viewport slepen waardoor de drag-strook onbereikbaar wordt en het
 * paneel niet meer terug naar beneden kan.
 */
const DRAG_TOP_MIN_VISIBLE_PX = 4;

/**
 * Hoogte van de top-strook (drag-strip + icoon-strip met dock/sluit) die
 * altijd zichtbaar moet blijven wanneer het paneel naar beneden gesleept
 * wordt. Komt overeen met `floating-panel-drag-strip` (`var(--spacing-3)`)
 * plus de absoluut gepositioneerde `floating-panel-actions` (`var(--spacing-1)`
 * + knop-hoogte). Iets royaler dan strikt nodig zodat de hit-targets
 * comfortabel klikbaar blijven aan de viewport-onderrand.
 */
const DRAG_HEADER_VISIBLE_PX = 48;

/**
 * Minimumbreedte van het paneel die zichtbaar moet blijven aan de
 * linker- of rechterkant van de viewport tijdens slepen. Vermijdt dat de
 * gebruiker een paneel volledig buiten beeld sleept en het niet meer kan
 * terughalen.
 */
const DRAG_SIDE_VISIBLE_PX = 40;

/**
 * Generieke host-component voor een floating panel: smalle drag-strook
 * bovenaan + top-right icoon-strip (dock + close), en daaronder de
 * content-component via {@link NgComponentOutlet}. Wordt door
 * {@link FloatingPanelService} dynamisch in een CDK Overlay gemount.
 *
 * `CdkDrag` wordt via `hostDirectives` op het host-element gezet zodat er
 * geen extra wrapper nodig is. De smalle strook bovenaan is de enige
 * `cdkDragHandle` zodat klikken in de body niet als drag tellen.
 */
@Component({
  selector: 'floating-panel-host',
  templateUrl: './floating-panel-host.component.html',
  styleUrl: './floating-panel-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkDragHandle, NgComponentOutlet, NgTemplateOutlet, SharedModule],
  hostDirectives: [
    {
      directive: CdkDrag,
      inputs: ['cdkDragBoundary: cdkDragBoundary', 'cdkDragFreeDragPosition: cdkDragFreeDragPosition'],
      outputs: ['cdkDragStarted: cdkDragStarted'],
    },
  ],
  host: {
    'role': 'dialog',
    'aria-modal': 'false',
    '(pointerdown)': 'bringToFront.emit()',
    '(cdkDragStarted)': 'bringToFront.emit()',
    'cdkDragBoundary': 'body',
    '[class.floating-panel-host--sheet]': 'sheetMode()',
  },
})
export class FloatingPanelHostComponent {
  readonly componentType = input.required<Type<unknown>>();
  readonly contentInjector = input.required<Injector>();
  readonly closable = input(true);
  readonly dockable = input(true);
  readonly dockTab = input<FloatingPanelDockTab | undefined>(undefined);
  readonly hasDockTarget = input(false);
  /**
   * Phone bottom-sheet-modus (op open-moment beslist door de service): het
   * paneel hangt vast onderaan de viewport, de drag-strook verdwijnt en
   * CdkDrag wordt uitgeschakeld. Zie docs/MOBIEL.md.
   */
  readonly sheetMode = input(false);

  readonly closeRequested = output<void>();
  readonly dockRequested = output<void>();
  readonly bringToFront = output<void>();
  /**
   * Pointer-positie tijdens een actieve drag-operatie. Wordt door de
   * service gebruikt om te detecteren of het paneel boven de dock-zone
   * hangt — zodat we hover-feedback op de dock-strook kunnen tonen en op
   * release het paneel automatisch kunnen docken. Zie
   * `docs/dialog-placement.md` §11.5.
   */
  readonly dragPointerMoved = output<{ readonly x: number; readonly y: number }>();
  /**
   * Eind-positie van een drag-operatie (`dropPoint` van CdkDragEnd).
   * De service beslist op basis van deze positie of het paneel in de
   * dock-zone is gelost.
   */
  readonly dragPointerEnded = output<{ readonly x: number; readonly y: number }>();

  protected readonly showDockButton = computed(() => !this.sheetMode() && this.dockable() && this.hasDockTarget());

  protected readonly tooltipClose = 'Sluiten';
  protected readonly tooltipDock = 'Vastzetten in zijbalk';
  protected readonly ariaLabelClose = 'Sluit paneel';
  protected readonly ariaLabelDock = 'Zet paneel vast in zijbalk';

  protected readonly outletInjector = computed(() => this.contentInjector());

  /**
   * Extra content-specifieke icoon-knoppen voor de top-right actions-strook
   * (bv. een Compacte/Volledige info-toggle). Wordt door de content-component
   * (of zijn wrapper) gevuld via het {@link FLOATING_PANEL_HEADER_ACTIONS}
   * signaal in de content-injector — leeg wanneer geen content opt-in'd heeft.
   */
  protected readonly headerActions = computed<readonly FloatingPanelHeaderAction[]>(() => {
    const actionsSignal = this.contentInjector().get(FLOATING_PANEL_HEADER_ACTIONS, null);
    return actionsSignal?.() ?? [];
  });

  /**
   * Optionele content-slot links bovenaan in de topbar — typisch een
   * compacte secundaire identificatie (bv. circuit-vermelding). Wordt door de
   * content gevuld via het {@link FLOATING_PANEL_TOPBAR_LEFT} signaal in de
   * content-injector.
   */
  protected readonly topbarLeftTemplate = computed<TemplateRef<unknown> | null>(() => {
    const tplSignal = this.contentInjector().get(FLOATING_PANEL_TOPBAR_LEFT, null);
    return tplSignal?.() ?? null;
  });

  /** Het eigen paneel (slotknop, content-key); `null` buiten een service-paneel. */
  protected readonly panelRef = computed<FloatingPanelRef | null>(() => this.contentInjector().get(FLOATING_PANEL_REF, null));

  protected readonly lockTooltip = floatingPanelLockTooltip;
  protected readonly unlockTooltip = floatingPanelUnlockTooltip;

  private readonly drag = inject(CdkDrag);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);
  private readonly body = viewChild<ElementRef<HTMLElement>>('body');
  /** Leesplek per inhoud (zie `FloatingPanelOptions.contentKey`). */
  private readonly scrollTopByContent = new Map<string, number>();
  private shownContentKey: string | null = null;

  constructor() {
    // Leesplek per inhoud: de scroll-listener noteert de positie onder de
    // getoonde inhoud (buiten de zone, zoals in de dock: geen change detection
    // per scroll-event); wisselt de inhoud (een paneel dat de actieve chart
    // volgt), dan krijgt de nieuwe inhoud na het renderen zijn eigen positie
    // terug, of begint ze bovenaan.
    effect(onCleanup => {
      const body = this.body()?.nativeElement;
      if (!body) return;
      const remember = () => {
        if (this.shownContentKey !== null) this.scrollTopByContent.set(this.shownContentKey, body.scrollTop);
      };
      untracked(() => this.zone.runOutsideAngular(() => body.addEventListener('scroll', remember, { passive: true })));
      onCleanup(() => body.removeEventListener('scroll', remember));
    });
    effect(() => {
      const contentKey = this.panelRef()?.contentKey() ?? null;
      untracked(() => {
        const previous = this.shownContentKey;
        this.shownContentKey = contentKey;
        if (previous === null || contentKey === null || previous === contentKey) return;
        const scrollTop = this.scrollTopByContent.get(contentKey) ?? 0;
        afterNextRender(() => {
          const body = this.body()?.nativeElement;
          if (body) body.scrollTop = scrollTop;
        }, { injector: this.injector });
      });
    });

    // Sheet-modus: schakel CdkDrag volledig uit. Enkel de drag-strook uit de
    // template halen volstaat niet — zonder geregistreerde handle beschouwt
    // CdkDrag het hele host-element als sleepbaar.
    effect(() => {
      this.drag.disabled = this.sheetMode();
    });

    // Forward drag-pointer naar service via outputs zodat de dock-zone
    // hover-feedback en drop-to-dock kan triggeren. CdkDrag emit `moved`
    // tijdens slepen (met `pointerPosition`) en `ended` op release (met
    // `dropPoint`).
    this.drag.moved.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.dragPointerMoved.emit({ x: event.pointerPosition.x, y: event.pointerPosition.y });
    });
    this.drag.ended.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.dragPointerEnded.emit({ x: event.dropPoint.x, y: event.dropPoint.y });
    });

    // Voorkom dat de gebruiker het paneel buiten bereik sleept. CDK
    // interpreteert het returnpunt van `constrainPosition` als de
    // **gewenste top-left van het paneel** (zie cdk drag-ref bron: bij
    // gezette `constrainPosition` wordt `_initialDomRect` als offset
    // gebruikt i.p.v. `_pickupPositionOnPage`). We bepalen dus de
    // natuurlijke top-left via `pointer - pickup` zodat het paneel
    // meebeweegt met de cursor zoals voorheen, en klemmen enkel binnen
    // viewport-grenzen:
    //   - bovenaan: top blijft minstens `DRAG_TOP_MIN_VISIBLE_PX` onder
    //     viewport-rand zodat de drag-strook altijd grabbaar blijft;
    //   - onderaan: top mag niet voorbij `viewport - DRAG_HEADER_VISIBLE_PX`
    //     zakken, anders zouden drag-strook + dock/sluit-iconen onder de
    //     viewport verdwijnen;
    //   - links/rechts: minstens `DRAG_SIDE_VISIBLE_PX` van het paneel
    //     moet aan elke zijde binnen viewport blijven.
    this.drag.constrainPosition = (pointer: Point, _ref, dim: DOMRect, pickup: Point): Point => {
      const naturalX = pointer.x - pickup.x;
      const naturalY = pointer.y - pickup.y;
      const view = this.document.defaultView;
      const viewportWidth = view?.innerWidth ?? this.document.documentElement.clientWidth;
      const viewportHeight = view?.innerHeight ?? this.document.documentElement.clientHeight;

      const minX = Math.min(0, DRAG_SIDE_VISIBLE_PX - dim.width);
      const maxX = Math.max(minX, viewportWidth - DRAG_SIDE_VISIBLE_PX);
      const maxY = Math.max(
        DRAG_TOP_MIN_VISIBLE_PX,
        viewportHeight - DRAG_HEADER_VISIBLE_PX,
      );

      const clampedX = Math.min(Math.max(naturalX, minX), maxX);
      const clampedY = Math.min(Math.max(naturalY, DRAG_TOP_MIN_VISIBLE_PX), maxY);
      return { x: clampedX, y: clampedY };
    };
  }

  /**
   * Reset de CdkDrag-translate naar de oorsprong zodat het paneel exact op de
   * (eventueel net bijgewerkte) overlay-positie komt te staan. Aangeroepen door
   * de service bij {@link FloatingPanelRef.recenter} — anders zou een vroegere
   * drag-offset het hercentreren tenietdoen.
   */
  resetDragPosition(): void {
    this.drag.reset();
  }
}
